// Video's: een rij kaarten waarvan de middelste actief is en stil meespeelt, plus een eigen speler op volledig scherm.
// Geen TikTok-embed: de video komt uit Shopify Bestanden, de knop gaat naar het origineel op TikTok.

const reduceMotion = matchMedia('(prefers-reduced-motion: reduce)');
const saveData = () => Boolean(navigator.connection && navigator.connection.saveData);

function publish(name, data) {
  try {
    window.Shopify?.analytics?.publish?.(`seeger:${name}`, data);
  } catch (error) {
    // Meten mag de speler nooit breken.
  }
}

function load(video) {
  if (!video.src && video.dataset.src) video.src = video.dataset.src;
}

class VideoCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-track]');
    this.player = this.querySelector('[data-player]');
    this.feed = this.querySelector('[data-feed]');
    if (!this.track || !this.player || !this.feed) return;

    this.count = this.track.querySelectorAll('[data-item]').length;
    this.loop = this.classList.contains('vcar--loop');
    if (this.loop) this.cloneItems();
    this.cards = [...this.track.querySelectorAll('[data-item]')];
    this.slides = [...this.feed.querySelectorAll('[data-slide]')];
    this.muted = false;
    this.active = -1;
    this.current = -1;
    this.visible = false;

    this.addEventListener('click', (event) => this.onClick(event));
    this.track.addEventListener('scroll', () => this.onScroll(), { passive: true });
    this.player.addEventListener('close', () => this.onClose());
    this.player.addEventListener('keydown', (event) => this.onKey(event));
    this.player.addEventListener('submit', (event) => this.onAdd(event));

    new IntersectionObserver(([entry]) => {
      this.visible = entry.isIntersecting;
      this.updatePreview();
    }).observe(this);

    this.slideObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && this.player.open) this.activate(this.slides.indexOf(entry.target));
        });
      },
      { root: this.feed, threshold: 0.6 }
    );
    this.slides.forEach((slide) => {
      this.slideObserver.observe(slide);
      const video = slide.querySelector('video');
      video.addEventListener('timeupdate', () => {
        if (video.duration) slide.style.setProperty('--p', String(video.currentTime / video.duration));
      });
      video.addEventListener('ended', () => this.next(slide));
    });

    this.center(this.loop ? this.count : 0, 'instant');
    this.setActive();
  }

  // Oneindige rij: een kopie ervoor en erna. Na het vegen springt de rij ongemerkt terug naar het origineel.
  cloneItems() {
    const originals = [...this.track.children];
    const copy = () =>
      originals.map((item) => {
        const clone = item.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        clone.dataset.clone = '';
        clone.querySelectorAll('button').forEach((button) => button.setAttribute('tabindex', '-1'));
        return clone;
      });
    this.track.prepend(...copy());
    this.track.append(...copy());
  }

  pitch() {
    return this.cards.length > 1 ? this.cards[1].offsetLeft - this.cards[0].offsetLeft : 0;
  }

  center(index, behavior = 'smooth') {
    const card = this.cards[index];
    if (!card) return;
    const left = card.offsetLeft - (this.track.clientWidth - card.offsetWidth) / 2;
    this.track.scrollTo({ left, behavior });
  }

  nearest() {
    const middle = this.track.scrollLeft + this.track.clientWidth / 2;
    let best = 0;
    let distance = Infinity;
    this.cards.forEach((card, index) => {
      const d = Math.abs(card.offsetLeft + card.offsetWidth / 2 - middle);
      if (d < distance) {
        distance = d;
        best = index;
      }
    });
    return best;
  }

  onScroll() {
    if (!this.frame) {
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        this.setActive();
      });
    }
    clearTimeout(this.settle);
    this.settle = setTimeout(() => this.wrap(), 140);
  }

  wrap() {
    this.setActive();
    if (!this.loop) return;
    const index = this.nearest();
    if (index >= this.count && index < this.count * 2) return;
    const shift = index < this.count ? this.count : -this.count;
    this.track.scrollTo({ left: this.track.scrollLeft + shift * this.pitch(), behavior: 'instant' });
    this.setActive();
  }

  setActive() {
    const index = this.nearest();
    if (index === this.active) return;
    this.active = index;
    this.cards.forEach((card, i) => card.classList.toggle('is-active', i === index));
    this.updatePreview();
  }

  updatePreview() {
    const allowed = this.visible && !reduceMotion.matches && !saveData() && !this.player.open;
    this.cards.forEach((card, i) => {
      const video = card.querySelector('video');
      if (!video) return;
      if (allowed && i === this.active) {
        load(video);
        video.play().then(
          () => card.classList.add('is-playing'),
          () => card.classList.remove('is-playing')
        );
      } else if (!video.paused) {
        video.pause();
      }
    });
  }

  onClick(event) {
    const step = event.target.closest('[data-step]');
    if (step) {
      const target = this.active + Number(step.dataset.step);
      this.center(this.loop ? target : Math.max(0, Math.min(this.cards.length - 1, target)));
      return;
    }
    const opener = event.target.closest('[data-open]');
    if (opener) {
      this.opener = opener;
      this.open(Number(opener.dataset.open));
      return;
    }
    if (event.target.closest('[data-close]')) {
      this.player.close();
      this.onClose();
      return;
    }
    const feedStep = event.target.closest('[data-feed-step]');
    if (feedStep) {
      this.go(this.current + Number(feedStep.dataset.feedStep));
      return;
    }
    if (event.target.closest('[data-sound]')) {
      this.setMuted(!this.muted);
      return;
    }
    const toggle = event.target.closest('[data-toggle]');
    if (toggle) {
      const video = toggle.closest('[data-slide]').querySelector('video');
      if (video.paused) this.play(video);
      else video.pause();
      this.syncToggle(toggle, video);
    }
  }

  open(index) {
    this.current = -1;
    this.player.showModal();
    this.isOpen = true;
    document.documentElement.classList.add('vplayer-open');
    this.updatePreview();
    this.feed.scrollTo({ top: this.slides[index].offsetTop, behavior: 'instant' });
    this.activate(index);
  }

  onClose() {
    if (!this.isOpen) return;
    this.isOpen = false;
    document.documentElement.classList.remove('vplayer-open');
    this.slides.forEach((slide) => slide.querySelector('video').pause());
    this.current = -1;
    this.updatePreview();
    this.opener?.focus({ preventScroll: true });
  }

  activate(index) {
    if (index < 0 || index === this.current) return;
    this.current = index;
    this.slides.forEach((slide, i) => {
      const video = slide.querySelector('video');
      if (i === index) {
        load(video);
        video.currentTime = 0;
        this.play(video);
        this.syncToggle(slide.querySelector('[data-toggle]'), video);
        publish('video_view', { video: slide.dataset.handle, product_id: slide.dataset.productId || null });
      } else {
        video.pause();
      }
      if (Math.abs(i - index) === 1) video.preload = 'metadata';
    });
  }

  play(video) {
    video.muted = this.muted;
    video.play().catch(() => {
      // Zonder klik van de bezoeker mag een browser geen geluid afspelen: dan zonder geluid.
      if (!video.muted) {
        this.setMuted(true);
        video.play().catch(() => {});
      }
    });
  }

  setMuted(muted) {
    this.muted = muted;
    this.slides.forEach((slide) => {
      slide.querySelector('video').muted = muted;
      slide.querySelector('[data-sound]')?.setAttribute('aria-pressed', String(!muted));
    });
  }

  syncToggle(toggle, video) {
    if (!toggle) return;
    const paused = video.paused;
    toggle.setAttribute('aria-label', paused ? toggle.dataset.labelPlay : toggle.dataset.labelPause);
    toggle.closest('[data-slide]').classList.toggle('is-paused', paused);
  }

  go(index) {
    const slide = this.slides[index];
    if (slide) this.feed.scrollTo({ top: slide.offsetTop, behavior: reduceMotion.matches ? 'instant' : 'smooth' });
  }

  next(slide) {
    const index = this.slides.indexOf(slide);
    if (index < this.slides.length - 1) {
      this.go(index + 1);
    } else {
      const video = slide.querySelector('video');
      video.currentTime = 0;
      this.play(video);
    }
  }

  onKey(event) {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      this.go(this.current + (event.key === 'ArrowDown' ? 1 : -1));
    }
  }

  onAdd(event) {
    const form = event.target.closest('[data-video-add]');
    if (!form) return;
    const slide = form.closest('[data-slide]');
    publish('video_add_to_cart', { video: slide?.dataset.handle, product_id: slide?.dataset.productId || null });
  }
}

if (!customElements.get('video-carousel')) customElements.define('video-carousel', VideoCarousel);
